import { useCallback, useEffect, useRef, useState } from "react";
import {
  Paperclip,
  Send,
  Loader2,
  Smile,
  X,
  FileText,
  Image as ImageIcon,
  Music,
  Video as VideoIcon,
  Reply,
} from "lucide-react";
import type { WebchatApi } from "@/lib/api";
import type { WidgetMessage, MessageType } from "@/types";
import { useSession } from "@/store/session-store";
import { cn } from "@/lib/cn";
import { t } from "@/lib/i18n";

interface Props {
  api: WebchatApi | null;
}

const MAX_FILE_BYTES = 20 * 1024 * 1024;

const QUICK_EMOJIS = [
  "😀", "😂", "😍", "🥰", "😘", "😎", "🤔", "😅",
  "😢", "😭", "😡", "😴", "🤩", "🥳", "😱", "🙄",
  "❤️", "🔥", "👍", "👎", "👏", "🙏", "💪", "🎉",
  "✨", "💯", "✅", "❌", "⭐", "🚀", "💡", "📌",
  "🤝", "👋", "🙌", "💬", "📞", "📅", "📎", "🛒",
  "💰", "💳", "💵", "🎁", "📦", "🚚", "🏠", "🏢",
];

export function Composer({ api }: Props) {
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emojiBtnRef = useRef<HTMLDivElement>(null);

  const replyingTo = useSession((s) => s.replyingTo);
  const setReplyingTo = useSession((s) => s.setReplyingTo);
  const locale = useSession((s) => s.locale);
  const droppedFile = useSession((s) => s.droppedFile);
  const setDroppedFile = useSession((s) => s.setDroppedFile);
  const addMessage = useSession((s) => s.addMessage);
  const replaceMessage = useSession((s) => s.replaceMessage);
  const markMessageFailed = useSession((s) => s.markMessageFailed);
  const upgradeSession = useSession((s) => s.upgradeSession);

  // Auto-grow textarea — clear inline height when empty so the CSS
  // `min-h-9` rule wins (otherwise scrollHeight on first paint can come
  // back larger than one line, making the input look oversized).
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (!text) {
      ta.style.height = "";
      return;
    }
    ta.style.height = "auto";
    ta.style.height = Math.min(128, ta.scrollHeight) + "px";
  }, [text]);

  // Focus textarea when entering reply mode
  useEffect(() => {
    if (replyingTo) textareaRef.current?.focus();
  }, [replyingTo]);

  // Drag-and-drop bridge: chat-window puts the file in the store, we
  // hoist it into local pendingFile and clear the store slot.
  useEffect(() => {
    if (!droppedFile) return;
    setPendingFile(droppedFile);
    setDroppedFile(null);
  }, [droppedFile, setDroppedFile]);

  // Close emoji picker on outside click
  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!emojiBtnRef.current?.contains(e.target as Node)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [emojiOpen]);

  const sendText = useCallback(async () => {
    const value = text.trim();
    if (!value || !api || isSending) return;
    const tempId = `local-${Date.now()}`;
    const optimistic: WidgetMessage = {
      id: tempId,
      content: value,
      type: "text",
      direction: "inbound",
      status: "pending",
      timestamp: new Date().toISOString(),
      pending: true,
      replyTo: replyingTo
        ? {
            id: replyingTo.id,
            content: replyingTo.content,
            type: replyingTo.type,
          }
        : null,
    };
    addMessage(optimistic);
    setText("");
    const reply = replyingTo;
    setReplyingTo(null);
    setIsSending(true);
    try {
      const res = await api.postMessage({
        content: value,
        type: "text",
        replyToId: reply?.id,
      });
      if (res.sessionToken && res.conversationId) {
        upgradeSession({
          sessionToken: res.sessionToken,
          conversationId: res.conversationId,
        });
      }
      replaceMessage(tempId, {
        ...optimistic,
        id: res.messageId,
        status: (res.status as WidgetMessage["status"]) ?? "sent",
        timestamp: res.timestamp,
        pending: false,
      });
    } catch {
      markMessageFailed(tempId);
    } finally {
      setIsSending(false);
    }
  }, [
    api,
    text,
    isSending,
    addMessage,
    replaceMessage,
    markMessageFailed,
    replyingTo,
    setReplyingTo,
    upgradeSession,
  ]);

  const sendUpload = useCallback(
    async (file: File) => {
      if (!api) return;
      if (file.size > MAX_FILE_BYTES) {
        return;
      }
      const type = inferType(file.type);
      const tempId = `local-${Date.now()}`;
      const optimistic: WidgetMessage = {
        id: tempId,
        content: null,
        type,
        direction: "inbound",
        status: "pending",
        timestamp: new Date().toISOString(),
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        pending: true,
        replyTo: replyingTo
          ? {
              id: replyingTo.id,
              content: replyingTo.content,
              type: replyingTo.type,
            }
          : null,
      };
      addMessage(optimistic);
      const reply = replyingTo;
      setReplyingTo(null);
      setIsUploading(true);
      try {
        const upload = await api.uploadFile(file);
        const res = await api.postMessage({
          type,
          mediaUrl: upload.mediaUrl,
          mimeType: upload.mimeType,
          fileName: upload.fileName,
          replyToId: reply?.id,
        });
        if (res.sessionToken && res.conversationId) {
          upgradeSession({
            sessionToken: res.sessionToken,
            conversationId: res.conversationId,
          });
        }
        replaceMessage(tempId, {
          ...optimistic,
          id: res.messageId,
          mediaUrl: upload.mediaUrl,
          mimeType: upload.mimeType,
          fileName: upload.fileName,
          fileSize: upload.size,
          status: (res.status as WidgetMessage["status"]) ?? "sent",
          timestamp: res.timestamp,
          pending: false,
        });
      } catch {
        markMessageFailed(tempId);
      } finally {
        setIsUploading(false);
      }
    },
    [
      api,
      addMessage,
      replaceMessage,
      markMessageFailed,
      replyingTo,
      setReplyingTo,
      upgradeSession,
    ],
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setPendingFile(file);
  };

  const confirmPendingFile = useCallback(async () => {
    if (!pendingFile) return;
    const f = pendingFile;
    setPendingFile(null);
    await sendUpload(f);
  }, [pendingFile, sendUpload]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendText();
      return;
    }
    if (!api) return;
    if (typingTimeout.current) return;
    void api.typing(true);
    typingTimeout.current = setTimeout(() => {
      void api.typing(false);
      typingTimeout.current = null;
    }, 2000);
  };

  const insertEmoji = (emoji: string) => {
    setText((t) => t + emoji);
    textareaRef.current?.focus();
  };

  const busy = isSending || isUploading;

  return (
    <div className="border-t border-cz-border bg-cz-surface">
      {replyingTo && <ReplyPreview reply={replyingTo} onClose={() => setReplyingTo(null)} />}
      {pendingFile && (
        <AttachmentPreview
          file={pendingFile}
          onSend={() => void confirmPendingFile()}
          onClear={() => setPendingFile(null)}
          isUploading={isUploading}
        />
      )}
      <div className="px-2 py-2 flex items-end gap-1.5">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept="image/*,audio/*,video/*,application/pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.zip"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          aria-label="Anexar arquivo"
          className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-cz-fg-muted hover:text-cz-fg hover:bg-cz-surface-muted disabled:opacity-50"
        >
          <Paperclip className="h-4 w-4" />
        </button>

        <div ref={emojiBtnRef} className="relative">
          <button
            type="button"
            onClick={() => setEmojiOpen((v) => !v)}
            disabled={busy}
            aria-label="Emojis"
            className={cn(
              "h-9 w-9 shrink-0 rounded-full flex items-center justify-center hover:bg-cz-surface-muted disabled:opacity-50",
              emojiOpen ? "text-cz-primary" : "text-cz-fg-muted hover:text-cz-fg",
            )}
          >
            <Smile className="h-4 w-4" />
          </button>
          {emojiOpen && (
            <EmojiPickerLite onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("typeMessage", locale)}
          rows={1}
          disabled={busy}
          className="flex-1 resize-none bg-cz-surface-muted rounded-2xl px-3 py-2 text-sm text-cz-fg placeholder:text-cz-fg-muted outline-none max-h-32 min-h-9"
        />

        <button
          type="button"
          onClick={() => void sendText()}
          disabled={busy || !text.trim()}
          aria-label={t("send", locale)}
          className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center bg-cz-primary text-cz-on-primary disabled:opacity-40"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

// ── Reply preview pill ────────────────────────────────────────────────

function ReplyPreview({
  reply,
  onClose,
}: {
  reply: WidgetMessage;
  onClose: () => void;
}) {
  return (
    <div className="px-3 pt-2 -mb-1">
      <div className="flex items-start gap-2 px-2 py-1.5 rounded-md bg-cz-surface-muted border-l-2 border-cz-primary text-[11px]">
        <Reply className="h-3 w-3 text-cz-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-cz-fg-muted">Respondendo</p>
          <p className="truncate text-cz-fg-muted">
            {reply.content?.trim() || labelForType(reply.type)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancelar resposta"
          className="text-cz-fg-muted hover:text-cz-fg"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Attachment preview ────────────────────────────────────────────────

function AttachmentPreview({
  file,
  onSend,
  onClear,
  isUploading,
}: {
  file: File;
  onSend: () => void;
  onClear: () => void;
  isUploading: boolean;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const Icon = pickFileIcon(file.type, file.name);

  return (
    <div className="px-3 pt-2">
      <div className="flex items-center gap-2 p-2 rounded-md bg-cz-surface-muted border border-cz-border">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            className="h-12 w-12 rounded object-cover flex-shrink-0"
          />
        ) : (
          <div className="h-12 w-12 rounded bg-cz-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-cz-primary" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{file.name}</p>
          <p className="text-[10px] text-cz-fg-muted">{formatBytes(file.size)}</p>
        </div>
        <button
          type="button"
          onClick={onSend}
          disabled={isUploading}
          aria-label={t("sendAttachment", useSession.getState().locale)}
          className="h-8 w-8 rounded-full bg-cz-primary text-cz-on-primary flex items-center justify-center disabled:opacity-50"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={isUploading}
          aria-label="Remover"
          className="h-8 w-8 rounded-full text-cz-fg-muted hover:text-cz-fg hover:bg-cz-surface flex items-center justify-center disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Emoji picker (no external dep) ────────────────────────────────────

function EmojiPickerLite({
  onPick,
  onClose,
}: {
  onPick: (e: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="absolute bottom-full left-0 mb-2 z-10 w-64 rounded-lg bg-cz-surface border border-cz-border shadow-lg p-2">
      <div className="grid grid-cols-8 gap-0.5">
        {QUICK_EMOJIS.map((emo) => (
          <button
            key={emo}
            type="button"
            onClick={() => {
              onPick(emo);
              onClose();
            }}
            className="h-7 w-7 rounded hover:bg-cz-surface-muted flex items-center justify-center text-base"
          >
            {emo}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function inferType(mime: string): MessageType {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "document";
}

function pickFileIcon(
  mime?: string | null,
  fileName?: string | null,
): typeof FileText {
  const m = (mime ?? "").toLowerCase();
  const n = (fileName ?? "").toLowerCase();
  if (m.startsWith("image/") || /\.(png|jpg|jpeg|gif|webp|svg)$/.test(n))
    return ImageIcon;
  if (m.startsWith("audio/") || /\.(mp3|ogg|wav|m4a|opus)$/.test(n)) return Music;
  if (m.startsWith("video/") || /\.(mp4|webm|mov|avi)$/.test(n)) return VideoIcon;
  return FileText;
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  const v = bytes / Math.pow(k, i);
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function labelForType(type: WidgetMessage["type"]): string {
  switch (type) {
    case "image":
      return "📷 Imagem";
    case "audio":
      return "🎤 Áudio";
    case "video":
      return "🎬 Vídeo";
    case "document":
      return "📎 Arquivo";
    default:
      return "Mensagem";
  }
}
